import {
	useGenerateReport,
	useLoanAudit,
	useRecentLookups,
} from "@homesite/hooks";
import type {
	EventTimelineItem,
	LoanAuditSummary,
	RespaChecklistProps,
} from "@homesite/ui";
import { LoanAuditPage, useToast } from "@homesite/ui";
import { createRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { env } from "../lib/env";
import { rootRoute } from "./__root";

const defaultRespa: RespaChecklistProps = {
	services: [],
	completedCount: 0,
	totalServices: 13,
	compliant: false,
};

const defaultSummary: LoanAuditSummary = {
	eventCount: 0,
	verifiedCount: 0,
	anchoredCount: 0,
	pendingCount: 0,
	respaCompliant: false,
	respaCompletedCount: 0,
	firstEventAt: null,
	lastEventAt: null,
};

export const loanAuditRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/loans/$loanId",
	component: function LoanAudit() {
		const { loanId } = loanAuditRoute.useParams();
		const { summary, respa, events, loading, error, summaryError } =
			useLoanAudit(loanId);
		const { generate, isPending: reportPending } = useGenerateReport(
			env.VITE_API_URL,
		);
		const { toast } = useToast();
		const { remember } = useRecentLookups();

		useEffect(() => {
			// Remember the lookup whenever the page resolved to something the
			// auditor can act on — partial data counts, only a fatal error does not.
			if (!loading && error == null) {
				remember(loanId);
			}
		}, [loading, error, loanId, remember]);

		const respaProps: RespaChecklistProps = respa
			? {
					services: respa.services.map((s) => ({
						code: s.code,
						name: s.name,
						completed: s.completed,
						eventCount: s.eventCount,
						lastEventAt: s.lastEventAt ?? null,
					})),
					completedCount: respa.completedCount,
					totalServices: respa.totalServices,
					compliant: respa.respaCompliant,
				}
			: defaultRespa;

		const summaryProps: LoanAuditSummary = summary
			? {
					eventCount: summary.eventCount,
					verifiedCount: summary.verifiedCount,
					anchoredCount: summary.anchoredCount,
					pendingCount: summary.pendingCount,
					respaCompliant: summary.respaCompliant,
					respaCompletedCount: summary.respaCompletedCount,
					firstEventAt: summary.firstEventAt ?? null,
					lastEventAt: summary.lastEventAt ?? null,
				}
			: defaultSummary;

		const timeline: EventTimelineItem[] = events.map((e) => ({
			id: e.hash,
			eventType: e.eventType,
			respaCode: e.respaCode,
			respaName: e.respaName,
			hash: e.hash,
			timestamp: e.timestamp,
			verified: e.verified,
			anchored: e.anchored,
			actorIsUserOfRecord: e.actorIsUserOfRecord ?? null,
			actorRole: e.actorRole,
		}));

		function handleGenerateReport(): void {
			generate({
				format: "pdf",
				kind: "loan",
				target: loanId,
				dateFrom: null,
				dateTo: null,
			})
				.then(() => {
					const filename = `homesite-loan-${loanId.slice(0, 10)}-all.pdf`;
					toast.success("Certificate downloaded", { description: filename });
				})
				.catch((err: unknown) => {
					const message =
						err instanceof Error ? err.message : "Report generation failed";
					toast.error(message);
				});
		}

		return (
			<LoanAuditPage
				loanReference={loanId}
				loanIdHash={
					summary?.loanIdHash ??
					respa?.loanIdHash ??
					events[0]?.loanIdHash ??
					loanId
				}
				timeline={timeline}
				respa={respaProps}
				summary={summaryProps}
				summaryUnavailable={summary == null && summaryError != null}
				loading={loading}
				error={error}
				onGenerateReport={handleGenerateReport}
				reportPending={reportPending}
			/>
		);
	},
});
